import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUploader from "@/components/admin/ImageUploader";
import RepeaterList from "@/components/admin/RepeaterList";
import RichTextEditor from "@/components/admin/RichTextEditor";
import { ICON_NAMES } from "@/components/Icon";

const empty = { slug: "", title: "", subtitle: "", hero_image: "", body_html: "", meta_description: "", gallery: [], features: [], divider_heading: "", divider_logos: [] };

export default function AdminPages() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/admin/pages"); setItems(data || []); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it, gallery: it.gallery || [], features: it.features || [], divider_logos: it.divider_logos || [] }); setOpen(true); };

  const save = async () => {
    if (!form.slug || !form.title) { toast.error("Slug and title required"); return; }
    try { await api.post("/admin/pages", form); toast.success("Page saved"); setOpen(false); load(); }
    catch { toast.error("Save failed"); }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this page?")) return;
    await api.delete(`/admin/pages/${slug}`); toast.success("Deleted"); load();
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="eyebrow-bar"><span className="overline">CMS</span></span>
          <h1 className="font-display text-3xl font-extrabold mt-2 text-navy">Pages</h1>
          <p className="text-sm text-ink-muted mt-1">Edit any product/service/landing page — hero photo, gallery, feature blocks, HTML body.</p>
        </div>
        <Button data-testid="pages-add" className="btn-saffron rounded-full" onClick={openNew}><Plus className="h-4 w-4 mr-2"/>New page</Button>
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(p => (
          <div key={p.slug} className="card-flat p-5" data-testid={`page-card-${p.slug}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.18em] text-saffron">/p/{p.slug}</div>
                <div className="font-display font-bold text-lg truncate text-navy">{p.title}</div>
                <div className="text-sm text-ink-muted line-clamp-2">{(p.subtitle || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim()}</div>
              </div>
              <div className="flex gap-1.5">
                <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="h-4 w-4"/></Button></a>
                <Button size="sm" variant="ghost" data-testid={`page-edit-${p.slug}`} onClick={()=>openEdit(p)}><Pencil className="h-4 w-4"/></Button>
                <Button size="sm" variant="ghost" data-testid={`page-delete-${p.slug}`} onClick={()=>remove(p.slug)}><Trash2 className="h-4 w-4 text-red-500"/></Button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-10 text-ink-muted">No pages yet.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing ? "Edit" : "New"} page</DialogTitle></DialogHeader>
          <div className="space-y-5 max-h-[75vh] overflow-y-auto pr-1">
            <div className="grid sm:grid-cols-2 gap-4">
              <F label="Slug (URL)"><Input data-testid="page-form-slug" disabled={!!editing} value={form.slug} onChange={e=>setForm({...form, slug:e.target.value.toLowerCase().replace(/\s+/g,"-")})}/></F>
              <F label="Title"><Input data-testid="page-form-title" value={form.title} onChange={e=>setForm({...form, title:e.target.value})}/></F>
            </div>
            <F label="Subtitle (rich text)">
              <RichTextEditor testid="page-form-subtitle" value={form.subtitle} onChange={v => setForm({...form, subtitle: v})}/>
            </F>
            <ImageUploader testid="page-form-hero" label="Hero image (uploads from your computer)" value={form.hero_image} onChange={(v)=>setForm({...form, hero_image:v})}/>

            <div>
              <div className="font-display font-bold text-navy text-sm uppercase tracking-wider mb-3">Hero photo carousel (additional photos that slide under the hero — first one is the hero_image above)</div>
              <RepeaterList testid="page-form-gallery" addLabel="Add photo to carousel"
                items={(form.gallery || []).map((u, i) => ({ id: `g${i}`, url: u }))}
                onChange={(arr) => setForm({ ...form, gallery: arr.map(x => x.url || "") })}
                makeNew={() => ({ url: "" })}
                render={(it, idx, setI) => (
                  <ImageUploader testid={`page-gallery-${idx}`} label={`Carousel photo #${idx+1}`} value={it.url} onChange={v => setI({ url: v })}/>
                )}
              />
            </div>

            <div>
              <div className="font-display font-bold text-navy text-sm uppercase tracking-wider mb-3">Divider band (between hero & highlights — static logos + heading)</div>
              <F label="Divider heading text (e.g. 'Our trusted lending partners')">
                <Input data-testid="page-form-divider-heading" value={form.divider_heading || ""} onChange={e => setForm({...form, divider_heading: e.target.value})}/>
              </F>
              <RepeaterList testid="page-form-divider-logos" addLabel="Add logo / text"
                items={form.divider_logos || []} onChange={(arr) => setForm({ ...form, divider_logos: arr })}
                makeNew={() => ({ label: "", image: "" })}
                render={(it, idx, setI) => (
                  <div className="grid sm:grid-cols-12 gap-3 items-start">
                    <div className="sm:col-span-5"><Input data-testid={`page-divider-label-${idx}`} value={it.label || ""} onChange={e => setI({ label: e.target.value })} placeholder="Text label (shown if no logo)"/></div>
                    <div className="sm:col-span-7"><ImageUploader testid={`page-divider-img-${idx}`} label="Logo image (optional)" value={it.image} onChange={v => setI({ image: v })}/></div>
                  </div>
                )}
              />
            </div>

            <div>
              <div className="font-display font-bold text-navy text-sm uppercase tracking-wider mb-3">Feature blocks (icon + title + description)</div>
              <RepeaterList testid="page-form-features" addLabel="Add feature"
                items={form.features || []} onChange={(arr) => setForm({ ...form, features: arr })}
                makeNew={() => ({ icon: "Sparkles", title: "", desc: "" })}
                render={(it, idx, setI) => (
                  <div className="grid sm:grid-cols-12 gap-3">
                    <div className="sm:col-span-3">
                      <Select value={it.icon || "Sparkles"} onValueChange={(v) => setI({ icon: v })}>
                        <SelectTrigger data-testid={`page-feat-icon-${idx}`}><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-72">{ICON_NAMES.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="sm:col-span-3"><Input data-testid={`page-feat-title-${idx}`} value={it.title || ""} onChange={e => setI({ title: e.target.value })} placeholder="Title"/></div>
                    <div className="sm:col-span-6"><Input data-testid={`page-feat-desc-${idx}`} value={it.desc || ""} onChange={e => setI({ desc: e.target.value })} placeholder="Short description"/></div>
                  </div>
                )}
              />
            </div>

            <F label="Body (rich text — use the toolbar for formatting; perfect for long Privacy / Terms content)">
              <RichTextEditor testid="page-form-body" value={form.body_html} onChange={v => setForm({...form, body_html: v})} minHeight="min-h-[260px]"/>
            </F>
            <F label="Meta description"><Input data-testid="page-form-meta" value={form.meta_description} onChange={e=>setForm({...form, meta_description:e.target.value})}/></F>

            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white">
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button className="btn-saffron rounded-full" data-testid="page-form-save" onClick={save}>Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (<div className="space-y-1.5"><Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>{children}</div>);
}
